// Visual cue on the browser tab itself — title prefix + favicon
// badge — so an agent can spot "something happened" at a glance even
// when the admin tab is sitting in the background behind Gmail /
// Slack / a dozen others.
//
// Two surfaces:
//   1. <title>     "(3) PA Support Tickets"   ← what shows in the tab
//   2. <link rel=icon>  base SVG + red dot overlay   ← the favicon
//
// Both update together off the same `useUnreadCount()` data source —
// no extra polling, no extra state, just a derived effect.
//
// When the user comes back to the tab (visibility flips visible),
// we DON'T zero the badge — that's the bell's job (mark-as-read).
// The badge mirrors whatever `unread` reports. Otherwise a user who
// tabbed back to glance and tabbed away would lose the cue.
//
// Mount this once at AuthedLayout level (App.tsx) — same pattern as
// `useDesktopNotifications`. Anonymous routes (/login, /assist/:token)
// don't carry an unread state, so the hook never fires there.

import { useEffect } from 'react';
import { useUnreadCount } from '@/lib/queries';

const BASE_TITLE = 'PA Support Tickets';
const FAVICON_ID = 'pa-favicon';

// Cache the favicon URL we computed for a given (count, baseHref) pair
// so we don't redraw the canvas on every effect re-run. The keying lets
// the cache reuse the same data: URL across consecutive `unread=3`
// renders without touching the canvas.
const faviconCache = new Map<string, string>();

/** Build a data: URL favicon by drawing the base SVG/PNG icon on a
 *  canvas and stamping a small red dot in the bottom-right. Returns
 *  the data URL synchronously after the source image has loaded;
 *  callers pass a callback because canvas-from-image is intrinsically
 *  async. */
function buildBadgedFavicon(baseHref: string, count: number, onReady: (dataUrl: string) => void) {
  const cacheKey = `${baseHref}::${count}`;
  const cached = faviconCache.get(cacheKey);
  if (cached) {
    onReady(cached);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const size = 64; // render at 2x of the typical 32px favicon
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Background tile so the dot has enough contrast against any icon.
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      if (count > 0) {
        // Red dot in bottom-right, ~38% of canvas size. White ring
        // around it so it stays visible on dark + light favicons.
        const r = size * 0.22;
        const cx = size - r - 2;
        const cy = size - r - 2;
        ctx.beginPath();
        ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = '#dc2626'; // rose-600
        ctx.fill();
        // Number if it fits (1–9); over 9, show a plain dot. Avoids
        // squished "10" / "99+" at favicon resolutions.
        if (count > 0 && count <= 9) {
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${Math.round(r * 1.3)}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(count), cx, cy + 1);
        }
      }
      const dataUrl = canvas.toDataURL('image/png');
      faviconCache.set(cacheKey, dataUrl);
      onReady(dataUrl);
    } catch {
      // Canvas can be tainted by cross-origin sources — fall back
      // silently. The title prefix still does its job.
    }
  };
  img.onerror = () => {
    /* ignore — fallback is just no favicon change */
  };
  img.src = baseHref;
}

/** Locate (or create) the favicon <link> element and swap its href. */
function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link#${FAVICON_ID}`);
  if (!link) {
    // The index.html ships `<link rel="icon" type="image/svg+xml" href="/favicon.svg">`.
    // We replace it with a <link id="pa-favicon"> the first time we run so
    // subsequent updates touch the same element. Keeping a separate id
    // means we don't fight with whatever else the browser might do with
    // the original <link>.
    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existing) {
      existing.id = FAVICON_ID;
      link = existing;
    } else {
      link = document.createElement('link');
      link.id = FAVICON_ID;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
  }
  link.type = 'image/png';
  link.href = href;
}

/** Read the project's base favicon URL once. We snapshot this on
 *  first call because we'll mutate the <link> in place and lose the
 *  original href after. */
let cachedBaseHref: string | null = null;
function getBaseFaviconHref(): string {
  if (cachedBaseHref) return cachedBaseHref;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  cachedBaseHref = link?.href ?? '/favicon.svg';
  return cachedBaseHref;
}

export function useTabAttention(): void {
  const { data: unread = 0 } = useUnreadCount();

  // Title prefix. Idempotent: re-running with the same unread count
  // does nothing because the string match short-circuits.
  useEffect(() => {
    const desired = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${BASE_TITLE}` : BASE_TITLE;
    if (document.title !== desired) {
      document.title = desired;
    }
  }, [unread]);

  // Favicon badge.
  useEffect(() => {
    const base = getBaseFaviconHref();
    if (unread === 0) {
      // Restore the clean SVG by pointing at the original.
      setFavicon(base);
      return;
    }
    buildBadgedFavicon(base, unread, (dataUrl) => setFavicon(dataUrl));
  }, [unread]);

  // Reset on unmount so the login screen doesn't inherit a stale
  // "(3) …" title if the user signs out.
  useEffect(() => {
    return () => {
      document.title = BASE_TITLE;
      const base = getBaseFaviconHref();
      setFavicon(base);
    };
  }, []);
}
